/**
 * Admin navigation config — single source of truth for admin routes.
 *
 * Two things live here, and they are deliberately different:
 *
 *   ADMIN_NAV         el REGISTRO de rutas. Una entrada por pantalla real,
 *                     plana, con su ruta de verdad. Nada se declara aquí que
 *                     no exista en App.js.
 *   ADMIN_NAV_GROUPS  la ARQUITECTURA de información de la barra lateral:
 *                     qué se enseña, agrupado y en qué orden. Referencia las
 *                     entradas por `key`, así que las dos no pueden
 *                     desincronizarse.
 *
 * No toda pantalla del registro está en la barra lateral, y eso es
 * intencionado: algunas son la navegación interna de un módulo (las del PMS
 * viven en su propia barra de pestañas) y otras son infraestructura de un
 * módulo, no un destino del producto (las plantillas y los envíos WhatsApp sin
 * resolver se abren desde Conversaciones). Cada una lleva escrito debajo desde
 * dónde se llega. Una pantalla sin puerta de entrada sería una pantalla
 * muerta; una pantalla con dos puertas idénticas es la duplicación que este
 * fichero existe para evitar.
 */
import {
  ChartPieSlice, Bell, Funnel, House, FileText, Pulse, CalendarBlank,
  Users, Coin, ListChecks, Gear, Lightning, Briefcase, Receipt, ChatText,
  ClockCounterClockwise, Bed, Scales, ClipboardText, Broom, Wrench,
  WhatsappLogo, Sun, ChatCircleDots, BookOpenText, FilePdf,
} from "@phosphor-icons/react";

export const ADMIN_NAV = [
  // --- Centro de acción ----------------------------------------------------
  { key: "actionCenter", to: "/actions",           label: "Centro de acción",  icon: Lightning },
  // Alertas: se llega por la pestaña interna del Centro de acción
  // (/actions?tab=alerts). La ruta sigue viva para enlaces antiguos y para
  // constructor/marketing, que la consumen desde sus propios paneles.
  { key: "alerts",      to: "/alerts",             label: "Alertas",            icon: Bell },

  // --- PMS -----------------------------------------------------------------
  { key: "pmsToday",        to: "/pms/today",        label: "Hoy",             icon: Sun },
  { key: "pmsReservations", to: "/pms/reservations", label: "Reservas",        icon: ClipboardText },
  { key: "pmsGuests",       to: "/pms/guests",       label: "Huéspedes",       icon: Users },
  { key: "pmsHousekeeping", to: "/pms/housekeeping", label: "Limpieza",        icon: Broom },
  { key: "pmsIssues",       to: "/pms/issues",       label: "Incidencias",     icon: Wrench },
  { key: "pmsOwnerStays",   to: "/pms/owner-stays",  label: "Owner stays",     icon: Bed },
  { key: "villas",          to: "/villas",           label: "Villas",          icon: House },
  // Las cuatro siguientes se llegan por la barra de pestañas del PMS, que
  // acompaña a todas sus pantallas. No se repiten en la barra lateral.
  { key: "pmsOverview",     to: "/pms",              label: "Overview PMS",    icon: Bed },
  { key: "pmsCalendar",     to: "/pms/calendar",     label: "Calendario PMS",  icon: CalendarBlank },
  { key: "pmsAllocation",   to: "/pms/allocation",   label: "Allocation",      icon: Scales },
  { key: "pmsSettings",     to: "/pms/settings",     label: "Ajustes PMS",     icon: Gear },

  // --- Comercial -----------------------------------------------------------
  { key: "leads",       to: "/leads",              label: "Leads",              icon: Funnel },
  { key: "followUps",   to: "/follow-ups",         label: "Seguimientos",       icon: ClockCounterClockwise },
  { key: "conversations", to: "/conversaciones",   label: "Conversaciones",     icon: ChatCircleDots },
  // Infraestructura de Conversaciones: se abren desde su cabecera, no son un
  // destino de primer nivel.
  { key: "messageTemplates", to: "/admin/message-templates", label: "Plantillas de mensajes", icon: ChatText },
  { key: "whatsappPending",  to: "/admin/whatsapp-pendientes", label: "WhatsApp pendientes",  icon: WhatsappLogo },

  // --- Administración ------------------------------------------------------
  { key: "payments",    to: "/payments",           label: "Finanzas",           icon: Coin },
  { key: "gastos",      to: "/admin/gastos",       label: "Gastos internos",    icon: Receipt },
  { key: "documents",   to: "/documents",          label: "Data room",          icon: FileText },
  { key: "operaciones", to: "/admin/operaciones",  label: "Operaciones · inversores", icon: Briefcase },
  { key: "users",       to: "/users",              label: "Usuarios",           icon: Users },
  { key: "settings",    to: "/settings",           label: "Ajustes",            icon: Gear },

  // --- Dirección y sistema -------------------------------------------------
  { key: "dashboard",   to: "/dashboard",          label: "Panel ejecutivo",    icon: ChartPieSlice },
  { key: "calendar",    to: "/calendar",           label: "Calendario",         icon: CalendarBlank },
  { key: "tasks",       to: "/tasks",              label: "Tareas IA",          icon: ListChecks },
  { key: "automations", to: "/automations",        label: "Automatizaciones",   icon: Lightning },
  { key: "knowledge",   to: "/admin/conocimiento", label: "Base de conocimiento", icon: BookOpenText },
  // Lo que la IA comercial puede ENTREGAR (el dossier), junto a lo que puede
  // AFIRMAR: son las dos fronteras de lo que sale hacia un lead.
  { key: "salesAssets", to: "/admin/activos-comerciales", label: "Activos comerciales", icon: FilePdf },
  { key: "activity",    to: "/activity",           label: "Actividad",          icon: Pulse },
];

/**
 * La barra lateral del admin.
 *
 * Cuatro grupos de producto —lo que se hace ahora, lo que se opera, lo que se
 * vende y lo que se administra— más uno de dirección y sistema. El orden es el
 * del día de trabajo: primero qué necesita atención, luego la casa, luego la
 * venta.
 *
 * Un item puede ser una `key` del registro o un nodo con hijos. "Operaciones"
 * es lo segundo: limpieza, incidencias y owner stays son tres pantallas reales
 * y ninguna es "Operaciones", así que se agrupan bajo una entrada desplegable
 * en vez de inventar una sexta pantalla que no existe.
 *
 * Display-only: no cambia rutas, permisos ni qué pantallas existen.
 */
export const ADMIN_NAV_GROUPS = [
  { key: "accion",   label: "Centro de acción", items: ["actionCenter"] },
  // Comercial por delante del PMS: hoy el negocio es vender las villas, no
  // operarlas — el PMS todavía es una demo sin reservas reales. El orden de
  // la barra dice en qué se trabaja cada día, así que lo primero después del
  // Centro de acción es el CRM.
  { key: "comercial",      label: "Comercial",      items: ["leads", "followUps", "conversations"] },
  {
    key: "pms",
    label: "PMS",
    items: [
      "pmsToday",
      "pmsReservations",
      "pmsGuests",
      {
        key: "pmsOperaciones",
        label: "Operaciones",
        icon: Broom,
        items: ["pmsHousekeeping", "pmsIssues", "pmsOwnerStays"],
      },
      "villas",
    ],
  },
  { key: "administracion", label: "Administración", items: ["payments", "gastos", "documents", "operaciones", "users", "settings"] },
  // "Tareas IA" vive aquí, al final y por debajo de Comercial y PMS: sigue
  // siendo accesible, pero no compite con el trabajo del día.
  { key: "sistema",        label: "Dirección y sistema", items: ["dashboard", "calendar", "tasks", "automations", "knowledge", "salesAssets", "activity"] },
];

/** Las `key` que la barra lateral pinta, hijos incluidos. */
export function sidebarItemKeys() {
  const keys = [];
  for (const group of ADMIN_NAV_GROUPS) {
    for (const item of group.items) {
      if (typeof item === "string") keys.push(item);
      else keys.push(...item.items);
    }
  }
  return keys;
}

export const ADMIN_ACCENT = "#1C1C1E";
